import { Component, OnDestroy, OnInit, ElementRef, ViewChild, inject } from '@angular/core';
import { AppViewportScroller } from '../../../core/services';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';

import { AdminService } from '../services/admin.service';
import { isPhoneViewport } from '../../../core/utils/phone';
import { ModalService } from '../../../shared/modal';
import { AdminFilterField, countActiveFilters, openAdminFilters } from '../filters-sheet/admin-filters-sheet';
import { AdminUserRow, AdminUserTotals } from '../models/admin-user.model';
import { AdminPagination } from '../models/admin-pagination.model';
import { filterLabel } from '../utils/filter-label';

const PER_PAGE = 50;

/** Filters live in the URL (?q=&superadmins=true&page=), same convention as
 *  the other three lists. `q` matches name/email and organization alike
 *  (confirmed against the real API) it's how Organization Detail's
 *  "Members in Users" cross-link lands here pre-filtered, by setting `q`
 *  to the org's slug rather than a separate `organization` param. */
@Component({
  selector: 'app-admin-users',
  standalone: false,
  templateUrl: './admin-users.html',
  styleUrl: './admin-users.scss',
})
export class AdminUsers implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();

  rows: AdminUserRow[] = [];
  /** For the whole filtered list; null until the backend sends `totals`. */
  totals: AdminUserTotals | null = null;
  pagination: AdminPagination | null = null;
  isLoading = false;
  /** Until the first response: the Totals card shows a skeleton meanwhile. */
  hasLoadedOnce = false;

  search = '';
  staffOnly = false;
  page = 1;

  /** Phones get a Filters button and sheet instead of the inline filters. */
  readonly phone = isPhoneViewport();
  private readonly modals = inject(ModalService);

  private readonly filterFields: AdminFilterField[] = [
    { kind: 'segments', key: 'role', label: 'Users', options: [{ value: 'all', label: 'All users' }, { value: 'staff', label: 'Staff only' }] },
  ];
  private readonly filterDefaults = { role: 'all' };

  private get filterValues(): Record<string, string> {
    return { role: this.staffOnly ? 'staff' : 'all' };
  }

  /** Phones: every filter but the search, in a sheet; applied in one go. */
  openFilters(): void {
    openAdminFilters(this.modals, { fields: this.filterFields, values: this.filterValues, defaults: this.filterDefaults }, v => {
      this.staffOnly = v['role'] === 'staff';
      this.applyFilters();
    });
  }

  /** Badge on the phone Filters button: how many filters are set. */
  get activeFilterCount(): number {
    return countActiveFilters(this.filterValues, this.filterDefaults);
  }

  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.search = params.get('q') ?? '';
    this.staffOnly = params.get('superadmins') === 'true';
    this.page = Number(params.get('page')) || 1;

    this.reload$.pipe(
      debounceTime(250),
      switchMap(() => {
        this.isLoading = true;
        return this.adminService.getUsers({
          q: this.search.trim() || undefined,
          superadmins: this.staffOnly || undefined,
          page: this.page,
          perPage: PER_PAGE,
        });
      }),
      takeUntil(this._destroy$),
    ).subscribe(({ rows, totals, pagination }) => {
      this.rows = rows;
      this.totals = totals;
      this.pagination = pagination;
      this.isLoading = false;
      this.hasLoadedOnce = true;
    });

    this.reload$.next();
  }

  /**
   * Organization column, split over two lines: the org on top, the role
   * muted underneath. Was one string with the role in brackets, which read
   * as part of the name. A user in more than one org shows the first plus a
   * count, because the full join wrapped the column.
   */
  orgName(user: AdminUserRow): string {
    if (user.superadmin) return 'Platform staff';
    if (!user.organizations.length) return '-';
    const [first, ...rest] = user.organizations;
    return rest.length ? `${first.name} +${rest.length}` : first.name;
  }

  orgRole(user: AdminUserRow): string {
    if (user.superadmin || !user.organizations.length) return '';
    const role = user.organizations[0].role;
    return role ? role.charAt(0).toUpperCase() + role.slice(1) : '';
  }

  /** The org chip's own link target (its slug), for the first org shown
   *  same one orgName()/orgRole() describe. */
  orgSlug(user: AdminUserRow): string | null {
    return user.organizations[0]?.slug ?? null;
  }

  /**
   * How the user signs in. A dash here read as missing data, but no SSO
   * provider means they sign in with a password the column header says
   * "Sign-in", so say that.
   */
  signIn(user: AdminUserRow): string {
    if (!user.ssoProviders.length) return 'Password';
    return user.ssoProviders.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
  }

  private applyFilters(): void {
    this.page = 1;
    this.syncUrl();
  }

  onSearchChange(value: string): void {
    this.search = value;
    this.applyFilters();
  }

  onStaffOnlyChange(value: string): void {
    this.staffOnly = value === 'staff';
    this.applyFilters();
  }

  @ViewChild('listCard') private listCard?: ElementRef<HTMLElement>;
  private readonly scroller = inject(AppViewportScroller);

  goToPage(page: number): void {
    if (page < 1 || (this.pagination && page > this.pagination.pages)) return;
    this.page = page;
    // Next/Previous land on the top of the list, not the top of the page.
    this.scroller.overrideNextScroll(() => this.listCard?.nativeElement.scrollIntoView({ block: 'start' }));
    this.syncUrl();
  }

  clearFilters(): void {
    this.search = '';
    this.staffOnly = false;
    this.applyFilters();
  }

  get totalsFilterLabel(): string | null {
    return filterLabel([
      this.search.trim() && `“${this.search.trim()}”`,
      this.staffOnly && 'staff only',
    ]);
  }

  get hasFilters(): boolean {
    return !!this.search || this.staffOnly;
  }

  private syncUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: this.search.trim() || null,
        superadmins: this.staffOnly ? 'true' : null,
        page: this.page > 1 ? this.page : null,
      },
      replaceUrl: true,
    });
    this.reload$.next();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
